import {
  useCurrentUser,
  useOrganization,
  useOrgMembers,
  useOrgRole,
} from "../../identity";
import { useWorkspace } from "../../workspaces";
import {
  PageHeading,
  PanelCard,
  PanelSkeleton,
  QueryBoundary,
} from "../../components/dashboard";
import { CopyButton } from "../../components/controls/CopyButton";
import { OrgRoleBadge } from "../../components/identity/OrgRoleBadge";

/* Organization overview: identity of the org you're operating in (read-only —
 * renaming is out of scope for v1), plus where you stand in it. */
export default function OrganizationPage() {
  const org = useOrganization();
  const members = useOrgMembers();
  const role = useOrgRole();
  const me = useCurrentUser();
  const { workspaces } = useWorkspace();

  return (
    <div className="flex flex-col gap-5">
      <PageHeading
        title="Organization"
        subtitle="Your organization's identity, people, and workspaces."
      />

      <QueryBoundary state={org} skeleton={<PanelSkeleton rows={4} />}>
        {(data) => (
          <div className="grid max-w-3xl grid-cols-1 gap-5">
            <PanelCard title="Details">
              <dl className="divide-y">
                <DetailRow label="Name">
                  <span className="font-medium text-fg-1">{data.name}</span>
                </DetailRow>
                <DetailRow label="Organization ID">
                  <span className="inline-flex items-center gap-1">
                    <code className="font-mono text-fg-2">{data.id}</code>
                    <CopyButton value={data.id} label="Copy organization ID" />
                  </span>
                </DetailRow>
                <DetailRow label="Your role">
                  <OrgRoleBadge role={role} />
                  <span className="ml-2 text-fg-3">{me.email}</span>
                </DetailRow>
                <DetailRow label="Members">
                  <span className="text-fg-1">
                    {members.data
                      ? members.data.length
                      : members.error
                        ? "Unavailable"
                        : "—"}
                  </span>
                  {/* No "Manage members" link: the Members page stays unlinked
                      this release (OPSS-329). */}
                </DetailRow>
                <DetailRow label="Workspaces">
                  <span className="text-fg-1">{workspaces.length}</span>
                  <span className="ml-2 text-fg-3">
                    {workspaces.map((w) => w.name).join(", ")}
                  </span>
                </DetailRow>
              </dl>
            </PanelCard>
          </div>
        )}
      </QueryBoundary>
    </div>
  );
}

function DetailRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-4 py-3 first:pt-0 last:pb-0">
      <dt className="w-40 shrink-0 text-body-sm text-fg-3">{label}</dt>
      <dd className="flex min-w-0 flex-1 items-center text-body-sm">
        {children}
      </dd>
    </div>
  );
}
